import React, { memo, useCallback } from 'react';
import { Button, Tooltip } from 'antd';
import {
  AudioOutlined,
  PushpinFilled,
  PushpinOutlined,
  ShareAltOutlined,
  UserOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import { I18n } from '../../../../helpers/i18n';

// Shared pin control for user-box (during share) and any share PiP fallback.
// Positioning / z-index come from .session-video-user-menu in SCSS.
export function SessionVideoPinButton({ isPinned = false, onClick }) {
  const pinLabel = isPinned ? I18n.t('video_unpin_screen') : I18n.t('video_pin_screen');
  const handleClick = (e) => {
    e.stopPropagation();
    onClick && onClick(e);
  };
  return (
    <Tooltip title={pinLabel}>
      <Button
        shape="circle"
        size="small"
        className="session-video-user-menu"
        aria-label={pinLabel}
        onClick={handleClick}
        icon={
          isPinned ? (
            <PushpinFilled style={{ fontSize: 12, color: '#69c0ff' }} />
          ) : (
            <PushpinOutlined style={{ fontSize: 12, color: 'rgba(255, 255, 255, 0.9)' }} />
          )
        }
      />
    </Tooltip>
  );
}

function SessionVideoUserBox({
  user,
  isMain = false,
  isRail = false,
  isInMain = false,
  layoutVariant = '',
  isSharing = false,
  isPinned = false,
  hidePin = false,
  currentUserId = null,
  onTogglePin,
  onAssignVideoRef,
  onAssignShareSlot = null,
  onRailSelect = null,
  isRailSelectable = false,
}) {
  const isVideoOn = !!(user.isVideoOn || user.bVideoOn);
  const isDeviceSharing = !!user.sharerOn;
  const handleVideoRef = useCallback(
    (el) => onAssignVideoRef && onAssignVideoRef(user.userId, el),
    [user.userId, onAssignVideoRef],
  );

  const handleRailClick = useCallback(() => {
    if (onRailSelect) onRailSelect(user);
  }, [onRailSelect, user]);

  const handleRailKeyDown = useCallback(
    (e) => {
      if (!onRailSelect) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onRailSelect(user);
      }
    },
    [onRailSelect, user],
  );

  const pinButton =
    !hidePin && onTogglePin ? (
      <SessionVideoPinButton isPinned={isPinned} onClick={() => onTogglePin(user)} />
    ) : null;

  const renderRailStateTile = () => {
    // Pinned on main: show camera/avatar; sticky share is a separate pane.
    if (isVideoOn) {
      return (
        <div className="session-video-rail-state">
          <VideoCameraOutlined style={{ fontSize: 28, color: '#4caf50' }} />
          <div className="session-video-avatar-icon small">
            <UserOutlined style={{ fontSize: 24, color: '#fff' }} />
          </div>
        </div>
      );
    }
    if (isDeviceSharing) {
      return (
        <div className="session-video-rail-state">
          <ShareAltOutlined style={{ fontSize: 32, color: '#69c0ff' }} />
        </div>
      );
    }
    return (
      <div className="session-video-avatar-icon">
        <UserOutlined style={{ fontSize: 40, color: '#fff' }} />
      </div>
    );
  };

  const renderMedia = () => {
    if (isSharing) {
      return <div ref={onAssignShareSlot} className="session-video-share-slot" />;
    }
    if (isRail && isInMain) {
      return renderRailStateTile();
    }
    if (isVideoOn) {
      return (
        <video-player-container
          ref={handleVideoRef}
          className="session-video-player"
        />
      );
    }
    if (isMain) {
      return (
        <div className="session-video-main-media-slot">
          <div className="session-video-avatar-icon">
            <UserOutlined style={{ fontSize: 40, color: '#fff' }} />
          </div>
        </div>
      );
    }
    return (
      <div className="session-video-avatar-icon">
        <UserOutlined style={{ fontSize: 40, color: '#fff' }} />
      </div>
    );
  };

  return (
    <div
      className={`session-video-user-box${isMain ? ' main' : ''}${isRail ? ' rail' : ''}${
        isRailSelectable ? ' selectable' : ''
      }${isInMain ? ' in-main' : ''}${
        layoutVariant ? ` ${layoutVariant}` : ''
      }${isSharing ? ' is-sharing' : ''}`}
      role={onRailSelect ? 'button' : undefined}
      tabIndex={onRailSelect ? 0 : undefined}
      onClick={onRailSelect ? handleRailClick : undefined}
      onKeyDown={onRailSelect ? handleRailKeyDown : undefined}
    >
      {pinButton}
      <div className="session-video-username">
        {user.displayName || user.userId}
        {user.userId === currentUserId ? ' (You)' : ''}
        <span className="user-status-icon audio">
          <AudioOutlined
            style={{
              color: user.muted ? '#e53935' : '#4caf50',
              fontSize: 16,
            }}
          />
          {user.muted && <span className="icon-slash" />}
        </span>
      </div>
      {renderMedia()}
    </div>
  );
}

function userBoxPropsAreEqual(prev, next) {
  const prevVideo = !!(prev.user.isVideoOn || prev.user.bVideoOn);
  const nextVideo = !!(next.user.isVideoOn || next.user.bVideoOn);
  return (
    prev.user.userId === next.user.userId &&
    prev.user.muted === next.user.muted &&
    prev.user.displayName === next.user.displayName &&
    prevVideo === nextVideo &&
    !!prev.user.sharerOn === !!next.user.sharerOn &&
    prev.isMain === next.isMain &&
    prev.isRail === next.isRail &&
    prev.isInMain === next.isInMain &&
    prev.layoutVariant === next.layoutVariant &&
    prev.isSharing === next.isSharing &&
    prev.isPinned === next.isPinned &&
    prev.hidePin === next.hidePin &&
    prev.currentUserId === next.currentUserId &&
    prev.onTogglePin === next.onTogglePin &&
    prev.onAssignVideoRef === next.onAssignVideoRef &&
    prev.onAssignShareSlot === next.onAssignShareSlot &&
    prev.onRailSelect === next.onRailSelect &&
    prev.isRailSelectable === next.isRailSelectable
  );
}

export default memo(SessionVideoUserBox, userBoxPropsAreEqual);

export function SessionVideoUserListItem({ user, isPinned, isCurrentUser, onTogglePin }) {
  const pinLabel = isPinned ? I18n.t('video_unpin_screen') : I18n.t('video_pin_screen');
  const isVideoOn = !!(user.isVideoOn || user.bVideoOn);

  return (
    <div className="user-list-item">
      <div className="user-item-name">
        <div className="user-item-avatar-icon">
          <UserOutlined style={{ fontSize: 16, color: '#333' }} />
        </div>
        {user.displayName || user.userId}
        {isCurrentUser && ' (You)'}
      </div>
      <div className="user-item-status">
        <span className="user-status-icon audio">
          <AudioOutlined
            style={{
              color: user.muted ? '#e53935' : '#4caf50',
            }}
          />
          {user.muted && <span className="icon-slash" />}
        </span>
        <span className="user-status-icon video">
          <VideoCameraOutlined
            style={{
              color: isVideoOn ? '#4caf50' : '#e53935',
            }}
          />
          {!isVideoOn && <span className="icon-slash" />}
        </span>
        <Tooltip title={pinLabel} placement="bottomRight">
          <Button
            type="text"
            size="small"
            className="user-item-menu-button"
            aria-label={pinLabel}
            onClick={() => onTogglePin(user)}
            icon={
              isPinned ? (
                <PushpinFilled style={{ fontSize: 12, color: '#1890ff' }} />
              ) : (
                <PushpinOutlined style={{ fontSize: 12, color: '#999' }} />
              )
            }
          />
        </Tooltip>
      </div>
    </div>
  );
}
